/**
 * Forgot / Reset password pages — reuse whichever FT endpoint is published; FT stays untouched.
 * - /app/forgot-password    → email input → request reset link
 * - /app/reset-password?token=... → new-password form
 * The submit responses NEVER reveal whether an email is registered.
 */
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ft } from "../app/api";

const CTA = "linear-gradient(135deg,#D8A5C3 0%,#B49AD9 100%)";
const SHELL = "radial-gradient(60% 45% at 12% 8%, rgba(245,225,243,0.85) 0%, transparent 60%), radial-gradient(55% 45% at 92% 100%, rgba(228,220,240,0.75) 0%, transparent 65%), #FFFFFF";

function Shell({ title, subtitle, children }) {
    return (
        <div className="min-h-screen grid place-items-center px-6 py-10" style={{ background: SHELL }}>
            <div className="w-full max-w-md rounded-[28px] bg-white border border-[#EEE6F3] shadow-[0_30px_80px_-40px_rgba(63,45,72,0.18)] p-8 md:p-10 relative" data-testid="auth-card">
                <Link to="/" aria-label="Close and return home" data-testid="auth-close-btn"
                    className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-[#8677A0] hover:text-[#2A1F32] hover:bg-[#FBF7FB] transition">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </Link>
                <Link to="/" className="flex items-center justify-center gap-2 mb-6">
                    <img src="/femcare-logo-v2.png" alt="Femcare.life" className="h-10 w-10 object-cover object-top rounded-xl ring-1 ring-[#EEE6F3]" />
                    <span className="font-extrabold text-lg tracking-tight text-[#3F2D48]">
                        Femcare<span className="text-[#B49AD9]">.life</span>
                    </span>
                </Link>
                <h1 className="text-[26px] leading-tight font-extrabold text-[#2A1F32] text-center tracking-tight">{title}</h1>
                {subtitle && <p className="text-[13.5px] text-[#6D5C7A] text-center mt-2 leading-relaxed">{subtitle}</p>}
                <div className="mt-7">{children}</div>
            </div>
        </div>
    );
}

export function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    const submit = async (e) => {
        e.preventDefault();
        if (!emailValid) return;
        setBusy(true); setError(null);
        try {
            await ft.forgotPassword(email.trim());
            setSent(true);
        } catch (err) {
            // Even on failure, show the neutral confirmation to avoid enumeration.
            setSent(true);
        } finally { setBusy(false); }
    };
    return (
        <Shell title="Forgot your password?" subtitle="Enter the email on your Femcare account and we'll send you a reset link.">
            {sent ? (
                <div className="space-y-4 text-center" data-testid="forgot-sent">
                    <div className="rounded-xl bg-[#F1FCF3] border border-[#B7E7C0] px-4 py-3 text-sm text-[#1F6B39]">
                        If that email is registered, we've sent password reset instructions.
                    </div>
                    <p className="text-xs text-[#8677A0]">Check your inbox and spam folder. Codes typically arrive within a minute.</p>
                    <Link to="/app/login" className="inline-block font-semibold text-[#8F639B] hover:text-[#6C4B7C] hover:underline underline-offset-2" data-testid="back-to-login">← Back to sign in</Link>
                </div>
            ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
                    <label className="block">
                        <span className="text-[11px] font-bold text-[#6D5C7A] uppercase tracking-[0.14em]">Email</span>
                        <input type="email" required data-testid="forgot-email"
                            value={email} onChange={(e)=>setEmail(e.target.value)} autoFocus
                            placeholder="you@example.com" autoComplete="email"
                            className="mt-2 w-full rounded-xl border border-[#EEE6F3] bg-white px-4 py-3 text-[#2A1F32] placeholder:text-[#B7A9C3] focus:ring-2 focus:ring-[#D8C4E7] focus:border-[#B49AD9] focus:outline-none" />
                    </label>
                    {error && <p className="text-sm text-[#B04A6A]" data-testid="forgot-error">{error}</p>}
                    <button type="submit" disabled={busy || !emailValid} data-testid="forgot-submit"
                        className="w-full rounded-xl px-6 py-3.5 text-white font-semibold shadow-[0_10px_25px_-10px_rgba(180,154,217,0.7)] hover:shadow-[0_14px_28px_-12px_rgba(180,154,217,0.85)] hover:-translate-y-0.5 transition disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed"
                        style={{background: CTA}}>
                        {busy ? "Sending…" : "Send reset link"}
                    </button>
                    <div className="text-center text-sm text-[#6D5C7A]">
                        Remembered it? <Link to="/app/login" className="font-semibold text-[#8F639B] hover:text-[#6C4B7C] hover:underline underline-offset-2" data-testid="link-to-login">Sign in</Link>
                    </div>
                </form>
            )}
        </Shell>
    );
}

export function ResetPasswordPage() {
    const nav = useNavigate();
    const params = new URLSearchParams(useLocation().search);
    const token = params.get("token") || "";
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);
    const pwValid = pw.length >= 6;
    const match = pw && pw === confirm;
    const submit = async (e) => {
        e.preventDefault();
        if (!token) { setError("Reset link is missing or expired. Please request a new one."); return; }
        if (!pwValid || !match) return;
        setBusy(true); setError(null);
        try {
            await ft.resetPassword(token, pw);
            setDone(true);
            setTimeout(() => nav("/app/login", { replace: true }), 1600);
        } catch (err) {
            setError((err && err.message) || "This reset link is invalid or has expired.");
        } finally { setBusy(false); }
    };
    return (
        <Shell title="Set a new password" subtitle="Choose a new password for your Femcare account.">
            {done ? (
                <div className="rounded-xl bg-[#F1FCF3] border border-[#B7E7C0] px-4 py-3 text-sm text-[#1F6B39] text-center" data-testid="reset-done">
                    Password updated. Redirecting you to sign in…
                </div>
            ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                    <label className="block">
                        <span className="text-[11px] font-bold text-[#6D5C7A] uppercase tracking-[0.14em]">New password</span>
                        <input type="password" required data-testid="reset-password"
                            value={pw} onChange={(e)=>setPw(e.target.value)} autoFocus
                            placeholder="Minimum 6 characters" minLength={6} autoComplete="new-password"
                            className="mt-2 w-full rounded-xl border border-[#EEE6F3] bg-white px-4 py-3 text-[#2A1F32] placeholder:text-[#B7A9C3] focus:ring-2 focus:ring-[#D8C4E7] focus:border-[#B49AD9] focus:outline-none" />
                        {pw && !pwValid && <p className="text-[11px] text-[#B04A6A] mt-1">Password must be at least 6 characters</p>}
                    </label>
                    <label className="block">
                        <span className="text-[11px] font-bold text-[#6D5C7A] uppercase tracking-[0.14em]">Confirm password</span>
                        <input type="password" required data-testid="reset-confirm"
                            value={confirm} onChange={(e)=>setConfirm(e.target.value)}
                            placeholder="Re-enter password" minLength={6} autoComplete="new-password"
                            className="mt-2 w-full rounded-xl border border-[#EEE6F3] bg-white px-4 py-3 text-[#2A1F32] placeholder:text-[#B7A9C3] focus:ring-2 focus:ring-[#D8C4E7] focus:border-[#B49AD9] focus:outline-none" />
                        {confirm && !match && <p className="text-[11px] text-[#B04A6A] mt-1">Passwords don't match</p>}
                    </label>
                    {error && <p className="text-sm text-[#B04A6A]" data-testid="reset-error">{error}</p>}
                    <button type="submit" disabled={busy || !pwValid || !match} data-testid="reset-submit"
                        className="w-full rounded-xl px-6 py-3.5 text-white font-semibold shadow-[0_10px_25px_-10px_rgba(180,154,217,0.7)] hover:shadow-[0_14px_28px_-12px_rgba(180,154,217,0.85)] hover:-translate-y-0.5 transition disabled:opacity-50"
                        style={{background: CTA}}>
                        {busy ? "Updating…" : "Update password"}
                    </button>
                </form>
            )}
        </Shell>
    );
}
